import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import {httpsCallable} from "@firebase/functions";
import { db, functions } from "../../firebase/FirebaseProvider";
import { BadgeStatus, User, UserBadge } from "../types/Types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBed,
    faCheckCircle,
    faLocationDot,
    faCamera
} from "@fortawesome/free-solid-svg-icons";
import { getCountryFlag } from "../util/Util";
import { getBadgeIcon } from "../util/BadgeUtil";

interface Props {
    user: User | undefined;
}

function UserDetails(props: Props) {
    const [activeBadges, setActiveBadges] = useState<Array<UserBadge>>([]);
    const viewedUserId = props.user?.id;
    const viewedUserStats = props.user?.stats;

    useEffect(() => {
        if (!viewedUserId) {
            setActiveBadges([]);
            return;
        }
        (async () => {
            const badgesSnap = await getDocs(
                query(
                    collection(db, 'user-badges'),
                    where('userId', '==', viewedUserId),
                    where('status', '==', BadgeStatus.ACTIVE)
                )
            );
            const badges: Array<UserBadge> = [];
            badgesSnap.forEach(doc => badges.push({ ...doc.data(), id: doc.id } as UserBadge));
            badges.sort((a, b) => a.order - b.order);
            setActiveBadges(badges);
        })();
    }, [viewedUserId]);

    useEffect(() => {
        if (!viewedUserId
            || (viewedUserStats?.statsVersion === 12
                && viewedUserStats?.comments !== undefined
                && viewedUserStats?.likes !== undefined
                && viewedUserStats?.photos !== undefined
                && viewedUserStats?.visits !== undefined
                && viewedUserStats?.stays !== undefined
                && viewedUserStats?.checkIns !== undefined
                && viewedUserStats?.uniqueCheckIns !== undefined
                && viewedUserStats?.checkInTime !== undefined
                && viewedUserStats?.points !== undefined
                && viewedUserStats?.pointsTotal !== undefined)) {
            return;
        }

        (async () => {
            try {
                const ensureUserStats = httpsCallable(functions, 'ensureUserStats');
                await ensureUserStats({userId: viewedUserId});
            } catch (error) {
                console.error("Failed to calculate user stats", error);
            }
        })();
    }, [viewedUserId, viewedUserStats]);

    return (
        <>
            {props.user && <div className={"user-header"}>
                {props.user && <div>{props.user.name}</div>}
                <div style={{marginTop: "-0.4em"}}>
                    {!props.user && <div>...</div>}
                        {activeBadges.map(badge => (
                            <div key={badge.id ?? badge.badgeType} className={"mt-2"} style={{marginBottom: "-10px", fontSize: "0.5em"}}>
                                {badge.description} <FontAwesomeIcon icon={getBadgeIcon(badge.badgeType)} />
                            </div>
                        ))}
                    <div>
                        {(props.user && props.user.visitCount > 0) && <div className={"d-inline-block"} style={{fontSize: "0.5em"}}><b>{props.user.visitCount}</b> <FontAwesomeIcon icon={faCheckCircle} title="Visits" /></div>}
                        {(props.user && !props.user.visitCount) && <div className={"d-inline-block"} style={{fontSize: "0.5em"}}><b>0</b> <FontAwesomeIcon icon={faCheckCircle} title="Visits" /></div>}
                        {(props.user && props.user.sessionVerifiedCount > 0) &&
                            <>
                                <div className={"d-inline-block ml-1"} style={{fontSize: "0.5em"}}>- <b>{props.user.sessionVerifiedCount}</b> <FontAwesomeIcon icon={faLocationDot} title="Check-ins" /></div>
                                {/*<div className={"d-inline-block ml-1"} style={{fontSize: "0.5em"}}>- <b>{calculateAndFormatPoints(props.user)}</b> <FontAwesomeIcon icon={faCoins} /></div>*/}
                            </>
                        }
                        {(props.user && (props.user.stayCount || 0) > 0) &&
                            <div className={"d-inline-block ml-1"} style={{fontSize: "0.5em"}}>- <b>{props.user.stayCount}</b> <FontAwesomeIcon icon={faBed} title="Stays" /></div>
                        }
                        {(props.user && props.user.photoCount > 0) && <>
                            <div className={"d-inline-block ml-1"} style={{fontSize: "0.5em"}}>- <b>{props.user.photoCount}</b> <FontAwesomeIcon icon={faCamera} /></div>
                        </>}
                        {(props.user && props.user.countriesVisited && props.user.countriesVisited.length > 0) &&
                            <div className={"d-inline-block ml-1"} style={{fontSize: "0.6em"}}>
                                - {props.user.countriesVisited
                                    .filter(code => {
                                        // Only include valid country codes (3-letter uppercase codes)
                                        const validCodes = ['ENG', 'SCO', 'WAL', 'NIR', 'IRE', 'IOM', 'ESP'];
                                        return validCodes.includes(code);
                                    })
                                    .map((code, index) => (
                                        <span key={index} style={{marginRight: '0.2em'}}>{getCountryFlag(code)}</span>
                                    ))}
                            </div>
                        }
                    </div>
                </div>
            </div>}
        </>
    );
}

export default UserDetails;
